import React from 'react';
import CodeBlock from '../../components/common/CodeBlock';
import AuthorInfo from '../../components/common/AuthorInfo';
import { SectionHeading, SubHeading, Para, Mark, Code, Callout, Output, Pill, TwoUp, QA } from './articleKit';

type Author = { name: string; fullName: string; avatar?: string };

export const JSEvents: React.FC<{ author: Author }> = ({ author }) => (
 <div className="max-w-none">
  {/* Hero header */}
  <div className="mb-10 pb-6 border-b border-gray-200 dark:border-gray-700">
   <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">Event Handling</h1>
   <p className="text-lg leading-8 text-gray-600 dark:text-gray-400 mb-5">
    How JavaScript reacts to clicks, keys, and form submits — through listeners, the event object, propagation phases, and delegation. This is what makes pages interactive.
   </p>
   <AuthorInfo name={author.name} fullName={author.fullName} avatar={author.avatar} lastUpdated="June 2026" readTime="18 min read" />
  </div>

  {/* 1. The Real Problem */}
  <section className="mb-12">
   <SectionHeading n={1}>The Real Problem</SectionHeading>
   <Para>On Swiggy, Amazon, or Instagram a user clicks Add To Cart, submits a login form, types in search, scrolls a feed, uploads an image, presses Enter. How does JavaScript know something happened?</Para>
   <Para>It doesn&apos;t constantly scan the page. Instead: the <Mark>browser detects the event → notifies JavaScript → JavaScript executes logic</Mark>. That mechanism is event handling.</Para>
  </section>

  {/* 2. What Is An Event */}
  <section className="mb-12">
   <SectionHeading n={2}>What Is an Event?</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <p className="text-[15px] leading-7 text-gray-800 dark:text-gray-200">An <Mark>event</Mark> is a signal that something happened in the browser.</p>
   </div>
   <div className="flex flex-wrap gap-2">{['Mouse click', 'Keyboard press', 'Input change', 'Form submission', 'Page load', 'Scroll', 'Resize', 'Touch'].map((e) => <Pill key={e}>{e}</Pill>)}</div>
   <Para>The flow is always: user action → browser event → JavaScript reaction.</Para>
  </section>

  {/* 3. Event Driven Programming */}
  <section className="mb-12">
   <SectionHeading n={3}>Event-Driven Programming</SectionHeading>
   <Para>Instead of checking forever, JavaScript says &quot;notify me when it happens.&quot;</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `button.addEventListener("click", () => {\n  console.log("Clicked");\n});` }]} defaultLanguage="javascript" id="ev-driven" />
   <Para>The browser waits; JavaScript &quot;sleeps.&quot; When the click happens, the browser wakes JavaScript to run the callback.</Para>
  </section>

  {/* 4. Event Listener */}
  <section className="mb-12">
   <SectionHeading n={4}>The Event Listener</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `element.addEventListener(eventName, callback);\n\nbutton.addEventListener("click", () => {\n  console.log("Clicked");\n});` }]} defaultLanguage="javascript" id="ev-listener" />
   <div className="flex flex-wrap gap-2">{['Element', 'Event name', 'Callback function'].map((e) => <Pill key={e}>{e}</Pill>)}</div>
  </section>

  {/* 5. Event Listener Lifecycle */}
  <section className="mb-12">
   <SectionHeading n={5}>Event Listener Lifecycle</SectionHeading>
   <CodeBlock snippets={[{ language: 'text', code: `register listener → store callback → wait for event → execute callback` }]} defaultLanguage="text" id="ev-lifecycle" />
   <Para>Nothing runs until the event actually occurs.</Para>
  </section>

  {/* 6. Event Object */}
  <section className="mb-12">
   <SectionHeading n={6}>The Event Object</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `button.addEventListener("click", (event) => {\n  console.log(event);\n});` }]} defaultLanguage="javascript" id="ev-object" />
   <Para>The browser automatically passes an <Mark>event object</Mark> to the callback. It contains the target element, mouse position, keyboard info, timestamp, and event type.</Para>
  </section>

  {/* 7. event.target */}
  <section className="mb-12">
   <SectionHeading n={7}>event.target</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `button.addEventListener("click", event => {\n  console.log(event.target); // the actual element clicked\n});` }]} defaultLanguage="javascript" id="ev-target" />
   <Para>Production example: in a shopping cart, <Code>event.target</Code> tells you which button was clicked.</Para>
  </section>

  {/* 8. event.type */}
  <section className="mb-12">
   <SectionHeading n={8}>event.type</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `console.log(event.type); // "click"` }]} defaultLanguage="javascript" id="ev-type" />
   <Para>Useful when one handler serves many event types.</Para>
  </section>

  {/* 9. event.currentTarget */}
  <section className="mb-12">
   <SectionHeading n={9}>event.target vs event.currentTarget</SectionHeading>
   <TwoUp
    left={{ title: 'event.target', items: ['The actual element clicked', 'Could be a deep child'] }}
    right={{ title: 'event.currentTarget', items: ['The element that owns the listener', 'Where addEventListener was called'] }}
   />
   <Para>An interview favourite — they differ when the clicked element is nested inside the element that has the listener.</Para>
  </section>

  {/* 10. The Propagation Problem */}
  <section className="mb-12">
   <SectionHeading n={10}>The Propagation Problem</SectionHeading>
   <CodeBlock snippets={[{ language: 'html', code: `<div id="parent">\n  <button id="child">Click</button>\n</div>` }]} defaultLanguage="html" id="ev-prop-html" />
   <Para>When the button is clicked, should the parent know? Should the document know? The browser answers this with <Mark>event propagation</Mark>.</Para>
  </section>

  {/* 11-13. Three phases */}
  <section className="mb-12">
   <SectionHeading n={11}>The Three Phases</SectionHeading>
   <CodeBlock snippets={[{ language: 'text', code: `1) Capturing  : Document → HTML → Body → Parent → Child   (top → bottom)\n2) Target     : the event reaches the actual element\n3) Bubbling   : Child → Parent → Body → HTML → Document   (bottom → top)` }]} defaultLanguage="text" id="ev-phases" />
   <Para>An event travels down (capturing), reaches the target, then travels back up (bubbling).</Para>
  </section>

  {/* 14. Bubbling default */}
  <section className="mb-12">
   <SectionHeading n={14}>Event Bubbling (Default Behavior)</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `parent.addEventListener("click", () => console.log("Parent"));\nchild.addEventListener("click", () => console.log("Child"));` }]} defaultLanguage="javascript" id="ev-bubble" />
   <Output>{`Child\nParent`}</Output>
   <Para>The child fires first, then the event bubbles up to the parent.</Para>
  </section>

  {/* 15. Why bubbling exists */}
  <section className="mb-12">
   <SectionHeading n={15}>Why Bubbling Exists</SectionHeading>
   <Para>Because parent elements often care about child actions. A dropdown needs to know when one of its menu items is clicked so it can close. Bubbling lets the parent hear the event.</Para>
  </section>

  {/* 16. stopPropagation */}
  <section className="mb-12">
   <SectionHeading n={16}>stopPropagation()</SectionHeading>
   <Para>Sometimes a child click should NOT trigger the parent — e.g. a Delete button inside a card, where clicking the card opens the product but clicking Delete should only delete:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `button.addEventListener("click", event => {\n  event.stopPropagation(); // bubbling stops here\n});` }]} defaultLanguage="javascript" id="ev-stop" />
  </section>

  {/* 17. preventDefault */}
  <section className="mb-12">
   <SectionHeading n={17}>preventDefault()</SectionHeading>
   <Para>Some elements have default browser actions — form submit, anchor navigation, checkbox toggle. <Code>preventDefault()</Code> cancels them:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `form.addEventListener("submit", event => {\n  event.preventDefault(); // stop the page reload\n});` }]} defaultLanguage="javascript" id="ev-prevent" />
   <Para>Most used in React forms, login pages, and search forms.</Para>
  </section>

  {/* 18-19. Event Delegation */}
  <section className="mb-12">
   <SectionHeading n={18}>Event Delegation</SectionHeading>
   <Para>Imagine 10,000 products. Attaching a listener to each is wasteful:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `// ❌ 10,000 listeners\nproduct1.addEventListener("click", ...);\nproduct2.addEventListener("click", ...);\n// ...` }]} defaultLanguage="javascript" id="ev-bad" />
   <Para>Instead attach <Mark>one</Mark> listener on the container and use the event target:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `container.addEventListener("click", event => {\n  if (event.target.matches(".product-btn")) {\n    console.log("Product Clicked");\n  }\n});` }]} defaultLanguage="javascript" id="ev-delegation" />
   <Para>This is <Mark>event delegation</Mark> — it uses bubbling + <Code>event.target</Code>.</Para>
  </section>

  {/* 20-21. Why delegation matters */}
  <section className="mb-12">
   <SectionHeading n={20}>Why Event Delegation Matters</SectionHeading>
   <TwoUp
    left={{ title: 'Performance', items: ['1 listener instead of thousands', 'Less memory', 'Less CPU & setup cost'] }}
    right={{ title: 'Dynamic DOM support', items: ['New items added later just work', 'No need to attach new listeners', 'Delegation already covers them'] }}
   />
   <Para>React, Angular, and Vue all heavily leverage delegation.</Para>
  </section>

  {/* 22-25. Common Events */}
  <section className="mb-12">
   <SectionHeading n={22}>Common Events</SectionHeading>
   <SubHeading>Mouse</SubHeading>
   <div className="flex flex-wrap gap-2 mb-3">{['click', 'dblclick', 'mouseenter', 'mouseleave', 'mousemove'].map((e) => <Pill key={e}>{e}</Pill>)}</div>
   <SubHeading>Keyboard</SubHeading>
   <div className="flex flex-wrap gap-2 mb-3">{['keydown', 'keyup', 'keypress'].map((e) => <Pill key={e}>{e}</Pill>)}</div>
   <SubHeading>Form</SubHeading>
   <div className="flex flex-wrap gap-2 mb-3">{['submit', 'change', 'input', 'focus', 'blur'].map((e) => <Pill key={e}>{e}</Pill>)}</div>
   <SubHeading>Browser</SubHeading>
   <div className="flex flex-wrap gap-2">{['load', 'resize', 'scroll'].map((e) => <Pill key={e}>{e}</Pill>)}</div>
  </section>

  {/* 26. React Connection */}
  <section className="mb-12">
   <SectionHeading n={26}>React Connection</SectionHeading>
   <Para>React doesn&apos;t attach listeners everywhere. It uses a few root listeners plus delegation and wraps native events in <Mark>synthetic events</Mark>, which reduces memory usage and normalizes behavior across browsers.</Para>
  </section>

  {/* 27-32. Common Interview Questions */}
  <section className="mb-12">
   <SectionHeading n={27}>Common Interview Questions</SectionHeading>
   <div className="flex flex-wrap gap-2 mb-5">
    {['target vs currentTarget?', 'What is bubbling?', 'What is capturing?', 'preventDefault vs stopPropagation?', 'What is event delegation?', 'Why is delegation better?'].map((q) => <Pill key={q}>{q}</Pill>)}
   </div>
   <QA q="What is the difference between target and currentTarget?">
    <Code>event.target</Code> is the actual element that triggered the event (possibly a deep child). <Code>event.currentTarget</Code> is the element the listener is attached to. They differ during bubbling/delegation.
   </QA>
   <QA q="What is the difference between preventDefault and stopPropagation?">
    <Code>preventDefault()</Code> cancels the browser&apos;s default action (form submit, link navigation) but lets the event keep propagating. <Code>stopPropagation()</Code> stops the event from traveling further through capturing/bubbling but doesn&apos;t cancel the default action. They&apos;re independent and can be used together.
   </QA>
   <QA q="What is event delegation and why is it better?">
    Attaching one listener to a parent and using <Code>event.target</Code>/<Code>matches()</Code> to handle child events as they bubble up. It uses far less memory than per-element listeners and automatically handles dynamically added elements.
   </QA>
   <QA q="What is the difference between bubbling and capturing?">
    Both are propagation phases. Capturing travels top-down (document → target) and bubbling travels bottom-up (target → document). By default listeners run in the bubbling phase; pass <Code>{'{ capture: true }'}</Code> to listen during capturing.
   </QA>
  </section>

  {/* 33. Real Production Example */}
  <section className="mb-12">
   <SectionHeading n={33}>Real Production Example</SectionHeading>
   <CodeBlock snippets={[{ language: 'text', code: `500 products → single listener on container → detect product click → open product` }]} defaultLanguage="text" id="ev-prod" />
   <CodeBlock snippets={[{ language: 'javascript', code: `list.addEventListener("click", event => {\n  const card = event.target.closest(".product-card");\n  if (card) openProduct(card.dataset.id);\n});` }]} defaultLanguage="javascript" id="ev-prod-code" />
   <Para>One listener instead of 500 — significant memory savings.</Para>
  </section>

  {/* Interview One-Liner */}
  <section className="mb-12">
   <SectionHeading n={34}>Interview One-Liner</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4">
    <p className="text-[15px] leading-7 font-medium text-gray-800 dark:text-gray-200">
     Event handling is the mechanism through which JavaScript responds to browser and user actions using event listeners, propagation phases, and callback execution.
    </p>
   </div>
  </section>

  {/* Most Important Takeaway */}
  <section className="mb-4">
   <SectionHeading n={35}>Most Important Takeaway</SectionHeading>
   <Callout tone="info" title="Takeaway">
    Events make web pages interactive. Understanding the event object, bubbling, capturing, <Code>preventDefault</Code>, <Code>stopPropagation</Code>, and event delegation is essential for frontend development, React internals, machine-coding rounds, and browser interviews.
   </Callout>
  </section>
 </div>
);

export default JSEvents;
